import React, { useCallback, useEffect } from 'react'
import { useField } from 'formik'
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { FormHelperText } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { SITE_RECAPTCHA_KEY } from '../../../constants'

type FormikRecaptchaProps = {
  name: string
}

const FormikRecaptchaField: React.FC<FormikRecaptchaProps> = props => {
  const { t } = useTranslation()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const { enqueueSnackbar } = useSnackbar()
  const [, { error, touched }, { setTouched, setValue, setError }] = useField(props.name)

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available')
      return
    }
    const token = await executeRecaptcha()
    setValue(token)
    setTouched(true)
    setError(undefined)
  }, [executeRecaptcha])

  useEffect(() => {
    handleReCaptchaVerify()
  }, [handleReCaptchaVerify])

  useEffect(() => {
    if (error) {
      handleReCaptchaVerify().then(() =>
        enqueueSnackbar(
          t('snackbarMessages.error.recaptchaExpired', {
            defaultValue: '{{error}}. Please try submitting the form again.',
            error,
          }),
          { variant: 'error', autoHideDuration: 2000 }
        )
      )
    }
  }, [error])

  return (
    <>
      <FormHelperText>
        <Trans
          i18nKey='formLabels.captcha'
          defaults={`This site is protected by reCAPTCHA and the Google <a1>Privacy Policy</a1> and <a2>Terms of Service</a2> apply.`}
          components={{
            a1: <a href='https://policies.google.com/privacy' target='_blank' />,
            a2: <a href='https://policies.google.com/terms' target='_blank' />,
          }}
        />
      </FormHelperText>
      {touched && error && (
        <FormHelperText className='px-14' error={true}>
          {error}
        </FormHelperText>
      )}
    </>
  )
}

// Scopes the reCAPTCHA script/provider to only the forms that actually need it, instead of
// loading it (671 KiB, ~350ms CPU) for every page via a root-level provider.
const FormikRecaptcha: React.FC<FormikRecaptchaProps> = props => {
  const { i18n } = useTranslation()

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={SITE_RECAPTCHA_KEY}
      language={i18n.language}
      useEnterprise={true}
      scriptProps={{
        async: true,
        defer: false,
        appendTo: 'head',
        nonce: undefined,
      }}
    >
      <FormikRecaptchaField {...props} />
    </GoogleReCaptchaProvider>
  )
}

export default FormikRecaptcha
